import { fmtNum } from '../lib/utils';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { kpiApi } from '../lib/api';

/**
 * Bind a KPI to a number, by choosing it from the list that is known to exist.
 *
 * The list is the SOURCE DATA CATALOGUE — the same one the Source Data tab
 * shows, built from the months that were actually imported. That matters for
 * two reasons:
 *
 *   1. everything offered here has been PROVED to resolve, because a file
 *      carried it. Typing a path by hand and hoping is what left KPIs silently
 *      scoring nothing at month end;
 *   2. it survives between months. A binding is set once and holds — the
 *      catalogue says what a number IS, and only its value changes when the
 *      next month is imported.
 *
 * Shape: one closed control that opens into a searchable list, rather than a
 * permanently-open 200-row table. The table made every KPI edit begin by
 * scrolling past a wall of paths; the thing a person wants is almost always
 * reachable by typing three letters of its name.
 *
 * Deliberately NOT filtered by department. A metric has no department — it is
 * a number the plant software reports, and the same figure legitimately feeds
 * KPIs in different departments (press strokes score Production and the Plant
 * Head alike). Section is the real grouping the source itself uses, so that is
 * what the chips filter on.
 */

// Which software's numbers are being listed, in the words the rest of the app
// uses for it. The `source` prop is the catalogue's own short key.
const SOURCE_TITLE = { ops: 'Operations', maint: 'Machine Maintenance',
                       npd: 'NPD', hr: 'HR software' };

// The height the list asks for. A module constant, not a value rebuilt each
// render: `place` is memoised with no dependencies and would otherwise close
// over the first render's copy.
const PANEL_H = 420;

const STATUS_PILL = {
  proxy: { text: 'proxy', bg: '#FEF6E7', fg: '#A16207',
    help: 'A stand-in, not a direct measurement — usually the PLANNED figure, '
        + 'because the source records no actual. It still scores; just read it knowing that.' },
  unavailable: { text: 'no data', bg: '#F1F3F7', fg: '#64748B',
    help: 'The source does not record this at all. A KPI bound to it can never be scored automatically.' },
};

// One request per source, shared by every picker on the page.
//
// This component is rendered once per KPI row in the KPI master, so fetching
// on mount un-shared meant fifty identical calls for the same catalogue the
// moment the tab opened. The cache holds the PROMISE rather than the result,
// so fifty pickers mounting in the same tick all await one in-flight request
// instead of starting a stampede.
//
// Lifetime is the page. A newly imported month is picked up on reload, which
// is also when the rest of the screen re-reads itself — and `reset` is here so
// the import flow can drop it deliberately if that ever stops being true.
const CATALOG = new Map();

export function resetMetricCatalogCache(source) {
  if (source) CATALOG.delete(source); else CATALOG.clear();
}

function loadCatalog(source) {
  if (!CATALOG.has(source)) {
    CATALOG.set(source, kpiApi.metricCatalog({ source })
      .then((r) => r.data)
      // A failed load must not be cached as a permanent failure — drop it so
      // the next picker to mount tries again.
      .catch((e) => { CATALOG.delete(source); throw e; }));
  }
  return CATALOG.get(source);
}

function fmtVal(v, uom) {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number') return String(v);
  const n = fmtNum(v);   // small values must survive — see fmtNum
  if (uom === '₹') return `₹${n}`;
  if (uom === '%') return `${n}%`;
  return uom ? `${n} ${uom}` : n;
}

export default function MetricPicker({ source, value, denominator, onChange,
                                       disabled = false, placeholder, filter }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [block, setBlock] = useState('');        // '' = every section
  const boxRef = useRef(null);
  const searchRef = useRef(null);
  const toggleRef = useRef(null);
  // The panel lives in a PORTAL, so it is not a DOM descendant of this
  // component. The outside-click handler therefore has to be told about it
  // explicitly — without this, every click inside the list (the search box, a
  // section chip, a row) reads as a click outside and shuts the list instantly.
  const panelRef = useRef(null);
  // The panel is positioned FIXED against the viewport, not absolutely inside
  // this component. In the KPI master the picker sits in a table wrapped in
  // `overflow-x-auto`, and a scroll container clips on both axes — an
  // absolutely-positioned panel would be cut off at the row instead of opening
  // over the page.
  const [pos, setPos] = useState(null);

  // The list ALWAYS opens directly under the control it belongs to. A list
  // floating in the middle of the screen loses its subject: you can no longer
  // see which field you are filling, and the form it covers is the one you were
  // reading. So instead of moving the list away from the control, the page is
  // scrolled so the control has room beneath it (see the open effect below).

  const place = useCallback(() => {
    const r = toggleRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(Math.max(r.width, 460), vw - 32);
    const below = vh - r.bottom - 12;
    const above = r.top - 12;
    // Below unless there is genuinely more room above — a flip on a near-tie
    // makes the list appear to jump sides as the page moves a few pixels.
    const flip = below < 220 && above > below;
    // Kept on screen horizontally: the control can sit inside a table that
    // scrolls sideways, so its left edge can be anywhere.
    const left = Math.min(Math.max(16, r.left), Math.max(16, vw - width - 16));
    setPos({
      left,
      width,
      ...(flip ? { bottom: vh - r.top + 6 } : { top: r.bottom + 6 }),
      maxHeight: Math.max(200, Math.min(PANEL_H, flip ? above : below)),
    });
  }, []);

  useEffect(() => {
    let dead = false;
    setData(null); setErr('');
    loadCatalog(source)
      .then((d) => { if (!dead) setData(d); })
      .catch((e) => { if (!dead) setErr(e?.response?.data?.detail || 'Could not read the source data'); });
    return () => { dead = true; };
  }, [source]);

  // Close on an outside click or Escape. Without this the panel stays open
  // behind the rest of the form and the next click lands somewhere unexpected.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inside = (boxRef.current && boxRef.current.contains(e.target))
        || (panelRef.current && panelRef.current.contains(e.target));
      if (!inside) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    // MAKE ROOM, rather than moving the list elsewhere. Opened near the foot of
    // a long form there is nowhere below to put 37 rows, and the two ways out
    // are both bad: a two-row slot, or a panel in the middle of the screen that
    // hides the field it belongs to. Scrolling the control up gives the list
    // its natural place and keeps the field in view above it.
    //
    // `scrollIntoView` walks whatever actually scrolls — the window here, an
    // inner container in the template table — which `window.scrollBy` cannot.
    const r = toggleRef.current?.getBoundingClientRect();
    if (r && window.innerHeight - r.bottom - 12 < Math.min(PANEL_H, window.innerHeight - 160)) {
      toggleRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    // After layout settles, so the rectangle read is the one the scroll left.
    const raf = requestAnimationFrame(place);
    place();
    searchRef.current?.focus();
    // `true` so scrolling of any ancestor — including the table's own
    // horizontal scroller — keeps the panel attached to its row.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // `filter` narrows what this picker may offer AT ALL — applied here rather
  // than at the search stage so the section chips and their counts describe the
  // list that can actually be chosen from. The figure builder uses it to keep
  // BUILT figures out: they are two of these divided, and the resolver reads
  // paths out of the imported file, so one built on another would resolve to
  // nothing every month.
  const all = useMemo(
    () => (data?.blocks || []).flatMap((b) => b.metrics.map(
      (m) => ({ ...m, block: b.block, blockLabel: b.block_label })))
      .filter((m) => !filter || filter(m)),
    [data, filter]);

  // A KPI bound to a BUILT figure stores the two paths the figure divides, not
  // the figure's own name — that is what lets the scorer read it with no
  // special case. So the row that is "selected" has to be recognised from the
  // pair, or every built figure would show as its numerator the moment the KPI
  // was reopened. `denominator` is optional: without it this behaves exactly as
  // it always did.
  const selected = useMemo(() => {
    if (denominator) {
      const built = all.find((m) => m.derived && m.derived.field === value
        && (m.derived.denominator_field || null) === (denominator || null));
      if (built) return built;
    }
    return all.find((m) => m.path === value) || null;
  }, [all, value, denominator]);

  // Counted from `all`, not from the block's own `count`: with a `filter` in
  // force those disagree, and a chip reading "Built figures 3" that opens onto
  // nothing is worse than no chip.
  const sections = useMemo(() => {
    const by = new Map();
    all.forEach((m) => {
      if (!by.has(m.block)) by.set(m.block, { key: m.block, label: m.blockLabel, count: 0 });
      by.get(m.block).count += 1;
    });
    return [...by.values()];
  }, [all]);

  // ---- what a figure is MADE OF -------------------------------------------
  // Two different things end up as "one number divided by another", and until
  // now neither said which two:
  //
  //   a BUILT figure — HR's own arithmetic over two of the source's paths. The
  //     control showed its stored `formula`, which is written in raw paths
  //     ("dashboard.detail.inspection.inspected_qty ÷ …"), so the one place
  //     that explained the sum explained it in the language nobody reads in;
  //   a RATIO KPI — a KPI with its own denominator. The box showed only the
  //     numerator, which is how Rejection Percentage came to display a RUPEE
  //     field under a KPI measured in %, with nothing on screen saying it was
  //     being divided by dispatch value at all.
  //
  // Both are answered the same way: name the top, name the bottom, and show
  // each one's path underneath so the binding is still verifiable.
  const byPath = useMemo(() => new Map(all.map((m) => [m.path, m])), [all]);

  const parts = useMemo(() => {
    const d = selected?.derived;
    // A built figure carries its own two halves. Otherwise the pair is the
    // KPI's own field over its own denominator — which only exists when the
    // caller passes one.
    const top = d ? d.field : (denominator ? value : null);
    const bottom = d ? (d.denominator_field || null) : (denominator || null);
    if (!top) return null;
    const name = (p) => byPath.get(p)?.label || p;
    return {
      built: !!d,
      top, bottom,
      topName: name(top),
      bottomName: bottom ? name(bottom) : null,
      topUom: byPath.get(top)?.uom || '',
      bottomUom: bottom ? (byPath.get(bottom)?.uom || '') : '',
    };
  }, [selected, denominator, value, byPath]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((m) => {
      if (block && m.block !== block) return false;
      if (!needle) return true;
      // Search the English name AND the raw path: people arriving from the
      // source app know the path, people arriving from the sheet know the name.
      return (m.label || '').toLowerCase().includes(needle)
          || (m.path || '').toLowerCase().includes(needle)
          || (m.blockLabel || '').toLowerCase().includes(needle);
    });
  }, [all, q, block]);

  // Grouped for display, preserving the catalogue's own section order.
  const grouped = useMemo(() => {
    const by = new Map();
    shown.forEach((m) => {
      if (!by.has(m.block)) by.set(m.block, { label: m.blockLabel, metrics: [] });
      by.get(m.block).metrics.push(m);
    });
    return [...by.entries()];
  }, [shown]);

  // The whole metric goes back alongside the path, never instead of it — every
  // caller already takes a path and must keep working untouched. The second
  // argument is what a BUILT figure needs: its path resolves to nothing in the
  // imported file, so a KPI binding to it has to store the two halves the
  // figure divides, which only the catalogue row knows.
  const choose = useCallback((m) => {
    onChange(m.path, m);
    setOpen(false);
    setQ('');
  }, [onChange]);

  return (
    <div ref={boxRef} className="relative max-w-2xl" data-testid="metric-picker">
      {/* Reads as a select, because that is what it is. Previously an unmapped
          KPI showed an amber warning panel and a mapped one a green-bordered
          card — two different shapes for one control, neither of which looked
          like something you open. */}
      <button ref={toggleRef} type="button" disabled={disabled}
        onClick={() => setOpen((v) => !v)} data-testid="metric-picker-toggle"
        aria-haspopup="listbox" aria-expanded={open}
        className={`w-full flex items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5
          text-left transition-colors
          ${open ? 'border-[#2563EB] ring-2 ring-[#2563EB]/15'
                 : value ? 'border-[#E6E9F0] hover:border-[#2563EB]'
                         : 'border-[#FBD9A8] hover:border-[#2563EB]'}
          ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
        <span className="flex-1 min-w-0">
          {value ? (
            <>
              <span className="block text-[13.5px] text-[#11182B] truncate font-medium">
                {selected?.label || 'Mapped'}
              </span>
              <span className="block font-mono text-[11px] text-[#15A36E] truncate">
                {selected?.derived ? selected.formula : value}
              </span>
            </>
          ) : (
            // The default wording is written for a KPI binding. The figure
            // builder is not binding anything, so it says what it is doing.
            <span className="text-[13.5px] text-[#B14A0E]">
              {placeholder || 'Choose the number this KPI reads…'}
            </span>
          )}
        </span>
        {/* The live figures, on the closed control. Which plant reports what is
            the fastest way to confirm the binding is the one you meant. */}
        {value && selected && !!Object.keys(selected.units || {}).length && (
          <span className="hidden sm:flex items-center gap-2 shrink-0 text-[11px] text-[#5A6377]">
            {Object.entries(selected.units).slice(0, 3).map(([u, v]) => (
              <span key={u} className="whitespace-nowrap">
                <span className="text-[#B4B9CC]">{u.replace(/\s*\(.*\)\s*$/, '')}</span>{' '}
                {fmtVal(v?.value, selected.uom)}
              </span>
            ))}
          </span>
        )}
        {value && !disabled && (
          <span role="button" tabIndex={-1} title="Clear the mapping"
            data-testid="metric-picker-clear"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="text-[#C9CFDC] hover:text-[#C2151F] leading-none text-base px-1">×</span>
        )}
        <span className={`shrink-0 text-[#98A1B2] text-[10px] transition-transform
          ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {/* The two figures behind the one above. Shown whenever there ARE two —
          a built figure or a KPI with a denominator — because "one KPI reads
          one number" stops being the whole truth the moment a division is
          involved, and the half that is not named is the half that gets
          misread. */}
      {parts && parts.bottom && (
        <div className="mt-1.5 rounded-lg border border-[#DCE6FB] bg-[#F7FAFF] px-3 py-2"
          data-testid="metric-picker-parts">
          <div className="text-[10px] uppercase tracking-wide text-[#8B8FA8] mb-1">
            {parts.built ? 'Built here from two figures' : 'This KPI divides two figures'}
          </div>
          {[['top', parts.topName, parts.top, parts.topUom],
            ['bottom', parts.bottomName, parts.bottom, parts.bottomUom]].map(
            ([which, label, path, uom], i) => (
            <div key={which}>
              {i === 1 && (
                <div className="text-[13px] text-[#5A6377] leading-none my-1" aria-hidden>÷</div>
              )}
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10px] uppercase text-[#B4B9CC] w-12 shrink-0">{which}</span>
                <span className="text-[12.5px] text-[#172554]">{label}</span>
                {uom && <span className="text-[11px] text-[#98A1B2]">({uom})</span>}
              </div>
              <div className="font-mono text-[10px] text-[#C9CFDC] pl-14 truncate"
                title={path}>{path}</div>
            </div>
          ))}
          <div className="text-[11px] text-[#5A6377] mt-1.5 pt-1.5 border-t border-[#EDF2FD]">
            {parts.topName} ÷ {parts.bottomName} × 100
            {/* The arithmetic is the server's. Saying so here stops this box
                being read as a second, independent calculation. */}
            <span className="text-[#98A1B2]"> — worked out by the same resolver that marks the month.</span>
          </div>
        </div>
      )}
      {/* A built figure that divides nothing still says where it reads from. */}
      {parts && !parts.bottom && parts.built && (
        <p className="mt-1.5 text-[11px] text-[#5A6377]" data-testid="metric-picker-parts">
          Built here, read straight from <span className="text-[#172554]">{parts.topName}</span>
          <span className="font-mono text-[10px] text-[#C9CFDC]"> ({parts.top})</span>
        </p>
      )}

      {/* A number that no longer arrives is still shown, and still says so.
          Silently dropping it would turn a broken binding into a blank mark. */}
      {selected?.missing_since && (
        <p className="text-[11px] text-[#B14A0E] mt-1">
          The source has stopped supplying this number. It will not score until it returns.
        </p>
      )}

      {/* RENDERED INTO <body>, not here.

          `position: fixed` is only relative to the viewport while no ancestor
          has a transform. This app's entrance animations — `.reveal`,
          `.enter-up` — animate `transform` with `fill-mode: both`, which gives
          the animated card a containing block of its own. The panel was then
          positioned against THAT card rather than the screen, so a list opened
          from a control on the left appeared adrift in the middle of the page.
          A portal escapes every such ancestor, and the coordinates `place()`
          measures finally mean what they say. */}
      {open && createPortal((
        <>
        {/* A backdrop, so the list reads as the one thing being used rather
            than as a tooltip floating over a live form — and so a click
            anywhere closes it, including on the table that scrolls underneath. */}
        <div className="fixed inset-0 z-50 bg-[#0B1220]/10"
          onMouseDown={() => setOpen(false)} aria-hidden />
        <div ref={panelRef}
          style={{ position: 'fixed', zIndex: 60,
                   ...(pos || { left: -9999, top: -9999 }) }}
          className="rounded-xl border border-[#D7E3FF] bg-white shadow-2xl overflow-hidden flex flex-col"
          data-testid="metric-picker-panel">
          <div className="p-2.5 border-b border-[#EEF1F6]">
            {/* Says what is being chosen and how many there are. A bare search
                box over an unlabelled list leaves the count — the one thing
                that tells you whether the source published anything — to be
                inferred from scrolling. */}
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-[11px] uppercase tracking-wide text-[#8B8FA8]">
                {SOURCE_TITLE[source] || 'Source'} numbers
              </span>
              <span className="text-[11px] text-[#B4B9CC]">
                {all.length} available{data?.period?.label ? ` · ${data.period.label}` : ''}
              </span>
              <button type="button" onClick={() => setOpen(false)}
                className="ml-auto text-[11px] text-[#98A1B2] hover:text-[#11182B]">close</button>
            </div>
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="metric-picker-search"
              placeholder="Search — try ppm, red tag, breakdown, dispatch…"
              className="w-full rounded-lg border border-[#E6E9F0] px-3 py-1.5 text-sm outline-none focus:border-[#2563EB]" />
            {sections.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-2">
                <button type="button" onClick={() => setBlock('')}
                  className={`text-[11px] rounded-full px-2 py-0.5 border ${!block
                    ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554]'
                    : 'bg-white border-[#E6E9F0] text-[#5A6377]'}`}>
                  All {all.length}
                </button>
                {sections.map((s) => (
                  <button key={s.key} type="button" onClick={() => setBlock(block === s.key ? '' : s.key)}
                    className={`text-[11px] rounded-full px-2 py-0.5 border ${block === s.key
                      ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554]'
                      : 'bg-white border-[#E6E9F0] text-[#5A6377]'}`}>
                    {s.label} {s.count}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {err && <p className="p-3 text-xs text-[#C2151F]">{err}</p>}
            {!err && !data && <p className="p-3 text-xs text-[#98A1B2]">Reading the source data…</p>}
            {!err && data && !shown.length && (
              <p className="p-3 text-xs text-[#98A1B2]">
                Nothing matches. Every number here comes from an imported month — if what you
                want is missing, import that software's month on the Source Data tab.
              </p>
            )}
            {grouped.map(([key, g]) => (
              <div key={key}>
                <div className="sticky top-0 bg-[#F8FAFC] px-3 py-1 text-[10px] uppercase tracking-wide text-[#98A1B2] border-b border-[#EEF1F6]">
                  {g.label}
                </div>
                {g.metrics.map((m) => {
                  const pill = STATUS_PILL[m.status];
                  // Compared against the ROW that resolved, not against the
                  // raw path: a KPI reading a built figure stores that
                  // figure's numerator, so matching on path alone would tick
                  // the numerator and leave the figure itself unticked.
                  const isSel = selected ? m.id === selected.id : m.path === value;
                  return (
                    <button key={m.id} type="button" onClick={() => choose(m)}
                      data-testid={`metric-picker-option-${m.path}`}
                      className={`w-full text-left px-3 py-2 border-b border-[#F2F4F8] hover:bg-[#F5F8FF]
                        ${isSel ? 'bg-[#E8EFFD]' : ''}`}>
                      <div className="flex items-start gap-2">
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] text-[#172554]">
                            {isSel && <span className="text-[#15A36E] mr-1">✓</span>}
                            {m.label}
                          </span>
                          {/* A built figure's path is a name HR coined, so it
                              explains nothing. Its sum does — and the sum is
                              written in the two figures' NAMES, because the
                              stored formula spells them as raw paths and a
                              list is the worst place to read a path. */}
                          <span className="block font-mono text-[10px] text-[#C9CFDC] truncate"
                            title={m.derived ? m.formula : m.path}>
                            {m.derived
                              ? (m.derived.denominator_field
                                  ? `${byPath.get(m.derived.field)?.label || m.derived.field}`
                                    + ` ÷ ${byPath.get(m.derived.denominator_field)?.label
                                            || m.derived.denominator_field} × 100`
                                  : `reads ${byPath.get(m.derived.field)?.label || m.derived.field}`)
                              : m.path}
                          </span>
                        </span>
                        {pill && (
                          <span title={pill.help} className="rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0"
                            style={{ background: pill.bg, color: pill.fg }}>{pill.text}</span>
                        )}
                      </div>
                      {/* The real figures, per plant. This is what tells you the
                          row is the one you meant — a name can be ambiguous, a
                          number against the plant you know rarely is. */}
                      <div className="mt-0.5 text-[11px] text-[#5A6377] flex flex-wrap gap-x-3">
                        {Object.entries(m.units || {}).map(([u, v]) => (
                          <span key={u}>
                            <span className="text-[#98A1B2]">{u}:</span> {fmtVal(v?.value, m.uom)}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {data?.period?.label && (
            <div className="px-3 py-1.5 text-[10px] text-[#98A1B2] border-t border-[#EEF1F6]">
              Figures shown are {data.period.label}. The binding does not change with the
              month — only the value does.
            </div>
          )}
        </div>
        </>
      ), document.body)}
    </div>
  );
}
